/**
 * Everything the settings screen needs from the gateway.
 *
 * Every response type here comes from `gateway-schema.d.ts`, which is generated
 * from the gateway's own Rust structs by `npm run gen:api` and gated in CI.
 * Nothing in this file describes a shape; it only names the endpoint that
 * returns one.
 *
 * That is the whole point. The first version of this file was hand-written, and
 * of roughly twenty field names in it five were wrong:
 *
 * | written        | actually sent  | what it looked like                    |
 * |----------------|----------------|----------------------------------------|
 * | `require_totp` | `enforce_2fa`  | 2FA reported as off while it was on     |
 * | `qr_code`      | `qr`           | the setup code rendered as nothing      |
 * | `otpauth_url`  | `otpauth_uri`  | likewise                                |
 * | `role_nav_tabs`| `role_permissions` | an empty permissions grid           |
 * | `last_seen_at` | `expires_at`   | a column reading "never seen" for all   |
 *
 * None of those failed. Each compiled, shipped, and displayed a confident wrong
 * answer, which is what a hand-written client buys you. Generating the names
 * makes that class of bug impossible rather than repeatedly corrected.
 *
 * ## Which store a setting belongs in
 *
 * There are two `tenant_configs` tables and they are not synced. Writing to the
 * wrong one saves happily and changes nothing, which is what the old settings
 * screen did with the phone-checking credentials for as long as it existed.
 *
 * | Key                | Read by     |
 * |--------------------|-------------|
 * | `hlr`, `apns`      | app server  |
 * | `clickhouse`       | gateway     |
 * | `security`         | gateway     |
 * | `worker_url`       | gateway     |
 * | `role_permissions` | gateway     |
 *
 * `hlr` and `apns` therefore go through the app server's own settings
 * endpoints, not through here. Anything added later must be checked the same
 * way: find who reads it, and write there.
 */
import type { components } from './gateway-schema';
import { GATEWAY_URL, ApiError, getToken } from './client';

type S = components['schemas'];

async function call<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`${GATEWAY_URL}${path}`, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getToken() ?? ''}`,
      ...(init?.headers ?? {}),
    },
  });
  const json = (await res.json().catch(() => ({}))) as T & {
    success?: boolean;
    error?: string;
  };
  // The gateway answers with `{success:false, error}` on a 200 as often as it
  // uses a status code, so both have to be treated as failure or a rejected
  // save looks like a successful one.
  if (!res.ok || json.success === false) {
    throw new ApiError(json.error ?? `Request failed (${res.status})`, res.status);
  }
  return json;
}

const get = <T>(path: string) => call<T>(path);
const post = <T>(path: string, body?: unknown) =>
  call<T>(path, { method: 'POST', body: JSON.stringify(body ?? {}) });
const put = <T>(path: string, body?: unknown) =>
  call<T>(path, { method: 'PUT', body: JSON.stringify(body ?? {}) });
const del = <T>(path: string) => call<T>(path, { method: 'DELETE' });

/* The names the pages already import, pointed at the generated shapes. */
export type Tenant = S['Tenant'];
export type TenantDetail = S['TenantDetail'];
export type GatewayAccount = S['Account'];
export type Session = S['Session'];
export type TotpStatus = S['TotpStatus'];
export type TotpQr = S['TotpQr'];
export type UserPermissions = S['UserPermissions'];
export type RolePermissions = S['RolePermissions'];
export type OrgSecurity = S['TenantSecurity'];

/* ---------- profile and password ---------- */

export const saveProfile = (first_name: string, last_name: string) =>
  put<S['Ok200']>('/auth/profile', { first_name, last_name });

export const changePassword = (current_password: string, new_password: string) =>
  post<S['Ok200']>('/auth/change-password', { current_password, new_password });

/* ---------- two-factor and sessions ---------- */

export const totpStatus = () => get<S['TotpStatus']>('/auth/totp/status');

export const totpShowQr = (password: string) => post<S['TotpQr']>('/auth/totp/show-qr', { password });

export const totpDisable = (password: string, code: string) =>
  post<S['Ok200']>('/auth/totp/disable', { password, code });

export const sessions = () => get<S['SessionList']>('/auth/sessions');
export const revokeSession = (publicId: string) =>
  del<S['Ok200']>(`/auth/sessions/${publicId}`);

/* ---------- organisations ---------- */

export const tenants = () => get<S['TenantList']>('/admin/tenants');
export const tenant = (id: string) => get<S['TenantDetail']>(`/admin/tenants/${id}`);

export const createTenant = (body: { name: string; slug: string; plan?: string }) =>
  post<S['TenantCreated']>('/admin/tenants', body);

export const updateTenant = (id: string, body: Partial<Tenant>) =>
  put<S['Ok200']>(`/admin/tenants/${id}`, body);

/** Write one service config. Only for keys the gateway itself reads. */
export const setTenantConfig = (id: string, key: string, value: unknown) =>
  post<S['Ok200']>(`/admin/tenants/${id}/config`, { key, value });

export const testClickHouse = (id: string) =>
  post<S['Ok200']>(`/admin/tenants/${id}/config/clickhouse/test`);

/** Whether everyone in this organisation must use two-factor. */
export const orgSecurity = (id: string) => get<S['TenantSecurity']>(`/admin/tenants/${id}/security`);
export const setOrgSecurity = (id: string, enforce_2fa: boolean) =>
  put<S['TenantSecurity']>(`/admin/tenants/${id}/security`, { enforce_2fa });

/* ---------- users ---------- */

export const allUsers = () => get<S['AccountList']>('/admin/users');
export const tenantUsers = (tenantId: string) =>
  get<S['AccountList']>(`/admin/tenants/${tenantId}/users`);

export const createUser = (
  tenantId: string,
  body: { email: string; first_name?: string; last_name?: string; role: string; password?: string },
) => post<S['AccountCreated']>(`/admin/tenants/${tenantId}/users`, body);

export const setUserRole = (userId: string, role: string) =>
  put<S['Ok200']>(`/admin/users/${userId}/role`, { role });

export const setUserStatus = (userId: string, status: string) =>
  put<S['Ok200']>(`/admin/users/${userId}/status`, { status });

export const moveUser = (userId: string, tenant_id: string) =>
  put<S['Ok200']>(`/admin/users/${userId}/tenant`, { tenant_id });

export const deleteUser = (userId: string) => del<S['Ok200']>(`/admin/users/${userId}`);

/* ---------- what a user may see ---------- */

/*
 * An empty list means every campaign, not none: the restriction is a set of
 * rows, and somebody with no rows is unrestricted. The setters take an array
 * and never null, because the gateway requires one: sending null to clear a
 * restriction returned "campaign_ids array is required" every time.
 */
export const userCampaigns = (userId: string) =>
  get<S['CampaignScope']>(`/admin/users/${userId}/campaigns`);
export const setUserCampaigns = (userId: string, campaign_ids: string[]) =>
  put<S['CampaignScope']>(`/admin/users/${userId}/campaigns`, { campaign_ids });

export const userBuyers = (userId: string) => get<S['BuyerScope']>(`/admin/users/${userId}/buyers`);
export const setUserBuyers = (userId: string, buyer_ids: string[]) =>
  put<S['BuyerScope']>(`/admin/users/${userId}/buyers`, { buyer_ids });

/** Suppliers are scoped the same way, which the first version of this missed. */
export const userSuppliers = (userId: string) =>
  get<S['SupplierScope']>(`/admin/users/${userId}/suppliers`);
export const setUserSuppliers = (userId: string, supplier_ids: string[]) =>
  put<S['SupplierScope']>(`/admin/users/${userId}/suppliers`, { supplier_ids });

/* ---------- permissions ---------- */

export const userPermissions = (userId: string) =>
  get<S['UserPermissions']>(`/admin/users/${userId}/permissions`);

/** A full replacement. Anything the server does not recognise is dropped. */
export const setUserPermissions = (
  userId: string,
  body: { nav_tabs: string[]; actions: string[] },
) => put<S['PermissionsSaved']>(`/admin/users/${userId}/permissions`, body);

/** Back to following the role, rather than pinned to a list. */
export const resetUserPermissions = (userId: string) =>
  del<S['Ok200']>(`/admin/users/${userId}/permissions`);

/* ---------- teams ---------- */

export const listTeams = (tenantId: string) => get<S['TeamList']>(`/admin/tenants/${tenantId}/teams`);
export const createTeam = (tenantId: string, name: string) =>
  post<S['Team']>(`/admin/tenants/${tenantId}/teams`, { name });
export const renameTeam = (teamId: string, name: string) =>
  call<S['Team']>(`/admin/teams/${teamId}`, { method: 'PATCH', body: JSON.stringify({ name }) });
export const deleteTeam = (teamId: string) => del<S['Ok200']>(`/admin/teams/${teamId}`);
/** A full replacement of who is in the team and who leads it. */
export const setTeamMembers = (teamId: string, members: S['TeamMemberInput'][]) =>
  put<S['Team']>(`/admin/teams/${teamId}/members`, { members });

/** `role_permissions` is the nav tabs map; `role_actions` is what each may do. */
export const rolePermissions = (tenantId: string) =>
  get<S['RolePermissions']>(`/admin/tenants/${tenantId}/role-permissions`);

/** Either map may be omitted; only the one sent is rewritten. */
export const setRolePermissions = (
  tenantId: string,
  body: {
    role_permissions?: Record<string, string[]>;
    role_actions?: Record<string, string[]>;
  },
) => put<S['Ok200']>(`/admin/tenants/${tenantId}/role-permissions`, body);
